/**
 * The Live tab: what is ACTUALLY happening today — the day in five tiles,
 * then one row per planned container (or the cards — the person's choice,
 * 20/10/2026), each with the strip of its drops, above the decision feed.
 * The timeline stays the blueprint; this is reality.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';

import type { DropGenEventRow, DropGenVerdictReport } from '@trafficpulse/shared';

import type { DayPlanContainerRow, DayPlanPreview } from '../../drop-generator.types';
import { useMinuteTick } from '../board-header/facts/use-minute-tick';
import { ContainerLiveCard, type LiveAutoSend } from './ContainerLiveCard';
import type { ContainerLiveActions } from './container/ContainerLiveBody';
import { ContainerLiveRow } from './container/ContainerLiveRow';
import { gatewaysFor } from './controls/GatewayBlocksLine';
import { DecisionsFeed } from './DecisionsFeed';
import { type LiveLayout, readLiveLayout, writeLiveLayout } from './layout/live-layout';
import { LiveHeader } from './layout/LiveHeader';
import { LiveEmpty, LiveFailed, LiveLoading } from './layout/LiveStates';
import { StripLegend } from './strip/StripLegend';
import { LiveSummary } from './summary/LiveSummary';
import { capCutsOf } from '../shared/cap/cap-cuts';

interface Props extends ContainerLiveActions {
  preview: DayPlanPreview | undefined;
  previewLoading: boolean;
  previewFailed?: boolean;
  onRetryPreview?: () => void;
  events: DropGenEventRow[] | undefined;
  eventsLoading: boolean;
  eventsFailed: boolean;
  countryKey: string;
  report?: DropGenVerdictReport | null;
  onEvaluate?: () => void;
  evaluating?: boolean;
  onEditContainer?: (containerId: string) => void;
  /** Phase 9 (Phase 20: per container) — each card's auto-send state. */
  autoSendOf?: (containerId: string) => LiveAutoSend;
}

/** A fully-depleted container keeps its card — the struck rows explain it. */
const plannedOf = (preview: DayPlanPreview | undefined): DayPlanContainerRow[] =>
  preview?.containers.filter((c) => c.drops.length > 0 || c.groups.some((g) => g.skipped.length > 0)) ?? [];

export const LiveTabCard: FC<Props> = ({ preview, previewLoading, previewFailed = false, onRetryPreview, events, eventsLoading, eventsFailed, countryKey, report, onEvaluate, evaluating = false, onEditContainer, autoSendOf, ...actions }) => {
  const [layout, setLayout] = useState<LiveLayout>(readLiveLayout);
  const now = useMinuteTick();
  const planned = plannedOf(preview);
  // A report of another day (simulated stepper) never paints today's cards.
  const sameDay = report?.runDate === preview?.runDate;
  const propsOf = (c: DayPlanContainerRow) => ({
    container: c,
    now,
    verdicts: sameDay ? report?.groups.filter((g) => g.containerId === c.containerId) : undefined,
    gateways: sameDay ? gatewaysFor(report?.gateways, c.containerId) : undefined,
    hits: sameDay ? report?.hits?.find((h) => h.containerId === c.containerId) : undefined,
    cuts: capCutsOf(preview?.warnings, c.containerId),
    autoSend: autoSendOf?.(c.containerId) ?? null,
    onEdit: onEditContainer,
    ...actions,
  });
  const choose = (next: LiveLayout) => {
    setLayout(next);
    writeLiveLayout(next);
  };
  return (
    <div className="flex flex-col gap-4">
      <LiveHeader layout={layout} onLayout={choose} draftsHref={preview ? `/smswiser/${countryKey}?view=prepared&date=${preview.runDate}` : null} onEvaluate={onEvaluate} evaluating={evaluating} />
      {previewLoading ? (
        <LiveLoading />
      ) : previewFailed && !preview ? (
        <LiveFailed onRetry={onRetryPreview} />
      ) : planned.length === 0 ? (
        <LiveEmpty />
      ) : (
        <>
          <LiveSummary containers={planned} now={now} />
          <StripLegend containers={planned} now={now} />
          {layout === 'rows' ? (
            <div className="flex flex-col gap-2" data-testid="live-rows">
              {planned.map((c) => <ContainerLiveRow key={c.containerId} {...propsOf(c)} />)}
            </div>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fit,minmax(330px,1fr))] items-start gap-4" data-testid="live-cards">
              {planned.map((c) => <ContainerLiveCard key={c.containerId} {...propsOf(c)} />)}
            </div>
          )}
        </>
      )}
      <DecisionsFeed events={events} loading={eventsLoading} failed={eventsFailed} />
    </div>
  );
};
